import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";
import { ApiService } from "./ApiService";

const STRIPE_CONNECT_STATUS_FIELDS = `
  accountId
  status
  chargesEnabled
  payoutsEnabled
  detailsSubmitted
  country
  currency
  onboardedAt
  connected
`;

const STRIPE_CONNECT_START = gql`
  mutation StripeConnectStart($params: StripeConnectStartInput!) {
    stripeConnectStart(params: $params) {
      code
      data {
        url
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STRIPE_CONNECT_COMPLETE = gql`
  mutation StripeConnectComplete($params: StripeConnectCompleteInput) {
    stripeConnectComplete(params: $params) {
      code
      data {
        ${STRIPE_CONNECT_STATUS_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STRIPE_CONNECT_DISCONNECT = gql`
  mutation StripeConnectDisconnect($params: StripeConnectStatusInput) {
    stripeConnectDisconnect(params: $params) {
      code
      data {
        ${STRIPE_CONNECT_STATUS_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const STRIPE_CONNECT_STATUS = gql`
  query StripeConnectStatus($params: StripeConnectStatusInput) {
    stripeConnectStatus(params: $params) {
      code
      data {
        ${STRIPE_CONNECT_STATUS_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface StripeConnectStatus {
  accountId?: string | null;
  status?: string | null;
  chargesEnabled?: boolean | null;
  payoutsEnabled?: boolean | null;
  detailsSubmitted?: boolean | null;
  country?: string | null;
  currency?: string | null;
  onboardedAt?: string | null;
  connected?: boolean | null;
}

export interface StripeConnectLink {
  url?: string | null;
}

export interface StripeConnectStartInput {
  returnUrl: string;
}

/**
 * Host Stripe Connect (Standard) — the account connects their OWN Stripe account
 * via OAuth so ticket money lands with them directly. Backed by ms_auth.
 */
export class StripeConnectService {
  /** Begin OAuth — returns the authorize URL to redirect the browser to. */
  static async start(returnUrl: string): Promise<OpResult> {
    return ApiService.executeMutation(STRIPE_CONNECT_START, {
      params: { returnUrl },
    });
  }

  /**
   * Finish OAuth, signed in, with the `code` + `state` Stripe handed back (`?stripe=confirm`). ms_auth exchanges the
   * code only when the signed-in account is the one that started — a link started by someone else is refused.
   */
  static async complete(code: string, state: string): Promise<OpResult> {
    return ApiService.executeMutation(STRIPE_CONNECT_COMPLETE, {
      params: { code, state },
    });
  }

  /** Current connection status. `refresh` re-pulls from Stripe first. */
  static async getStatus(refresh?: boolean): Promise<OpResult> {
    return ApiService.executeQuery(STRIPE_CONNECT_STATUS, {
      params: { refresh: !!refresh },
    });
  }

  /** Disconnect the connected account. */
  static async disconnect(): Promise<OpResult> {
    return ApiService.executeMutation(STRIPE_CONNECT_DISCONNECT, {
      params: {},
    });
  }
}
